import React, { FC, useMemo, useCallback, MouseEvent } from "react";
import _ from "lodash";
import classNames from "classnames";
import { connect, Dispatch, MapDispatchToPropsParam } from "react-redux";
import ListItemText from "@material-ui/core/ListItemText";
import { makeStyles, PopoverOrigin } from "@material-ui/core";
import Divider from "@material-ui/core/Divider";
import MenuList from "@material-ui/core/MenuList";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import { GridApi, ColumnApi } from "ag-grid-community";
import MoreMenu from "containers/legacy-cart/grid-components/MoreMenu";
import { format } from "lib/globalize-util";
import Button from "components/form/button";
import theme from "styles/theme";
import { IStore } from "reducers";
import cartGridViewActions, {
    createCustomGridView,
    deleteCustomGridView,
    updateCustomGridView,
} from "actions/cart-grid-view";
import type { IScope } from "reducers/scope";
import { GridViewStoredState } from "helpers/porduct-collection-types";
import { ICustomView, IPivot } from "reducers/cart-grid-view";
import { postToTopIframe } from "helpers/window-messages";
import { SAVE_SIZING_VIEW_MODAL } from "constants/cart";
import useGridStateHandlers from "containers/legacy-cart/hooks/useGridStateHandlers";
import { ALLOWED_FILTER_KEYS, mergeColumns } from "containers/legacy-cart/helpers/columnsHelper";
import { getPivotSettings } from "containers/legacy-cart/helpers/sizingViewHelper";
import { VIEW_MODE } from "types/cart/shipment-review";

interface IOwnProps {
    api: GridApi;
    columnApi: ColumnApi;
}

interface IStateProps {
    scope: IScope;
    customViews: ICustomView[];
    activeView: string;
    viewMode: VIEW_MODE;
    gridState: GridViewStoredState;
}

type IDispatchProps = {
    onUpdateView: (id: string, viewData: ICustomView) => void;
    onDeleteView: (id: string) => void;
    onUpdateState: (updatedState: GridViewStoredState) => void;
    onChangeActiveView: (activeView: string) => void;
    onChangeViewMode: (viewMode: VIEW_MODE) => void;
    onSaveDefaultView: (viewData: ICustomView) => void;
};

const anchorOrigin: PopoverOrigin = {
    vertical: "bottom",
    horizontal: "right",
};
const transformOrigin: PopoverOrigin = {
    vertical: "top",
    horizontal: "right",
};

export const ViewsToolPanel: FC<IOwnProps & IStateProps & IDispatchProps> = (props) => {
    const { api: gridApi, columnApi } = props;
    const { viewMode, activeView = null, customViews, gridState } = props;
    const { onUpdateView, onDeleteView, onUpdateState, onSaveDefaultView, onChangeActiveView, onChangeViewMode } =
        props;

    const classes = useStyles();

    const changeHandlers = useGridStateHandlers({
        gridColumnApi: columnApi,
        gridApi: gridApi,
        storedState: gridState,
    });

    const defaultView = useMemo<ICustomView>(
        () => ({
            _id: null,
            name: format("Default"),
            isDefault: true,
            columns: [],
            filters: {},
            pivot: { columns: [], mode: false },
            viewMode: VIEW_MODE.INITIAL,
        }),
        []
    );

    const views = useMemo<ICustomView[]>(() => {
        const [defaultViews, views] = _.partition(customViews, { isDefault: true });
        return [...(_.isEmpty(defaultViews) ? [defaultView] : defaultViews), ...views];
    }, [customViews, defaultView]);

    const moreMenuClasses = useMemo(() => ({ menu: { paper: classes.moreMenuPaper } }), [classes]);

    const handleSaveView = useCallback(
        () =>
            postToTopIframe({
                action: SAVE_SIZING_VIEW_MODAL,
                payload: formatView(
                    { ...defaultView, name: "", isDefault: false },
                    {
                        pivot: getPivotSettings(columnApi),
                        state: gridState,
                        viewMode,
                    }
                ),
            }),
        [columnApi, defaultView, gridState, viewMode]
    );

    const handleDeleteView = useCallback(
        (id: string, disabled: boolean) => (e: MouseEvent) => {
            e.stopPropagation();
            // Note: There is a bug in mui lib.
            // Clicking disabled item will trigger click event on the background component which can cause unexpected behaviour.
            // So we need to re-implement "disabled" logic manually
            if (!disabled && id) {
                onDeleteView(id);
            }
        },
        [onDeleteView]
    );

    const handleUpdateView = useCallback(
        (id: string, disabled: boolean) => (e: MouseEvent) => {
            e.stopPropagation();
            // Note: There is a bug in mui lib.
            // Clicking disabled item will trigger click event on the background component which can cause unexpected behaviour.
            // So we need to re-implement "disabled" logic manually
            if (!disabled) {
                const updates = {
                    pivot: getPivotSettings(columnApi),
                    state: gridState,
                    viewMode,
                };
                if (id) {
                    onUpdateView(id, formatView(_.find(views, { _id: id }), updates));
                } else {
                    onSaveDefaultView(formatView(defaultView, updates));
                }
            }
        },
        [columnApi, gridState, viewMode, onUpdateView, views, onSaveDefaultView, defaultView]
    );

    const handleApplyView = useCallback(
        (view: ICustomView) => () => {
            const updates = { activeView: null, gridState: {}, viewMode: VIEW_MODE.INITIAL };
            if (view.isDefault && !view._id) {
                changeHandlers.onResetGrid();
            } else {
                updates.activeView = view._id;
                updates.gridState = {
                    columns: mergeColumns(columnApi.getColumnState(), view.columns),
                    filterModel: getActiveFilters(view.filters),
                    toolpanel: { id: gridApi.getOpenedToolPanel() },
                    pivot: view.pivot || { columns: [], mode: false },
                };
                updates.viewMode = view.viewMode;
                changeHandlers.onSetGridState(updates.gridState);
            }
            onUpdateState(updates.gridState);
            onChangeActiveView(updates.activeView);
            onChangeViewMode(updates.viewMode);
        },
        [onUpdateState, onChangeActiveView, onChangeViewMode, changeHandlers, columnApi, gridApi]
    );

    return (
        <div className={classes.root}>
            <div className={classes.newView}>
                <Button className={classes.newViewButton} variant="outlined" color="primary" onClick={handleSaveView}>
                    {format("Save current view")}
                </Button>
            </div>
            <Divider />
            <MenuList>
                {_.map(views, (view) => (
                    <MenuItem
                        onClick={handleApplyView(view)}
                        key={view._id}
                        className={classNames(classes.viewItem, activeView === view._id && classes.activeView)}
                        disableRipple
                        disableTouchRipple
                    >
                        <ListItemText className={classes.viewHeader}>
                            {
                                <Tooltip
                                    title={<span className={classes.tooltip}>{view.name}</span>}
                                    placement="bottom-start"
                                    arrow
                                >
                                    <div className={classes.ellipsis}>
                                        <span>{view.name}</span>
                                    </div>
                                </Tooltip>
                            }
                        </ListItemText>
                        <MoreMenu
                            classes={moreMenuClasses}
                            anchorOrigin={anchorOrigin}
                            transformOrigin={transformOrigin}
                        >
                            <MenuItem
                                key="update"
                                className={classNames({ menuItemDisabled: activeView !== view._id })}
                                onClick={handleUpdateView(view._id, activeView !== view._id)}
                            >
                                {format("Update")}
                            </MenuItem>
                            <MenuItem
                                key="delete"
                                className={classNames({ menuItemDisabled: view?.isDefault })}
                                onClick={handleDeleteView(view._id, view?.isDefault)}
                            >
                                {format("Delete")}
                            </MenuItem>
                        </MoreMenu>
                    </MenuItem>
                ))}
            </MenuList>
        </div>
    );
};

const useStyles = makeStyles({
    root: {
        width: "100%",
    },
    newView: {
        width: "100%",
        height: 64,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    newViewButton: {
        width: 176,
        borderColor: theme.palette.grey,
        color: theme.palette.darkGrey,
        fontSize: 14,
    },
    activeView: {
        backgroundColor: theme.palette.grey,
    },
    tooltip: {
        fontSize: 14,
    },
    ellipsis: {
        position: "relative",
        height: 24,
        "&::before": {
            content: "&nbsp;",
            visibility: "hidden",
        },
        "& span": {
            position: "absolute",
            left: 0,
            right: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
    },
    viewItem: {
        "& .more-menu.active": {
            display: "block",
        },

        "&:hover": {
            "& .more-menu": {
                display: "block",
            },
        },
    },
    viewHeader: {
        "& span": {
            fontSize: 16,
        },
    },
    moreMenuPaper: {
        minWidth: 120,
        "& .MuiButtonBase-root": {
            fontSize: 14,
        },
        "& .menuItemDisabled": {
            opacity: 0.5,
            cursor: "default",
        },
    },
});

/**
 * Pick allowed column and filter data to save/update grid view
 */
const formatView = (
    view: ICustomView,
    updates: { pivot: IPivot; state: GridViewStoredState; viewMode: VIEW_MODE }
) => ({
    ...view,
    pivot: updates.pivot,
    columns: updates?.state.columns,
    filters: _.pick(updates?.state?.filterModel, ALLOWED_FILTER_KEYS),
    viewMode: updates?.viewMode,
});

const getActiveFilters = (activeFilters = {}) => _.pick(activeFilters, ALLOWED_FILTER_KEYS);

const mapStateToProps = ({ cartGridView, scope }: IStore): IStateProps => ({
    scope,
    customViews: cartGridView.customViews,
    activeView: cartGridView.activeView,
    viewMode: cartGridView.viewMode,
    gridState: cartGridView.gridState,
});

const mapDispatchToProps: MapDispatchToPropsParam<IDispatchProps, any> = (dispatch: Dispatch<any>) => ({
    onUpdateView: (id: string, viewData: ICustomView) => dispatch(updateCustomGridView(id, viewData)),
    onDeleteView: (id: string) => dispatch(deleteCustomGridView(id)),
    onUpdateState: (updatedState: GridViewStoredState) => dispatch(cartGridViewActions.updateState(updatedState, true)),
    onChangeViewMode: (viewMode) => dispatch(cartGridViewActions.update({ viewMode }, true)),
    onChangeActiveView: (activeView) => dispatch(cartGridViewActions.update({ activeView }, true)),
    onSaveDefaultView: (viewData: ICustomView) => dispatch(createCustomGridView(viewData)),
});

export default connect<IStateProps, IDispatchProps, any>(mapStateToProps, mapDispatchToProps)(ViewsToolPanel);
